import type { ComponentRef, ComponentType } from "react";
import type { View } from "react-native";
import DateField from "./fields/DateField";
import MoneyField from "./fields/MoneyField";
import MultiChipsField from "./fields/MultiChipsField";
import ProductField from "./fields/ProductField";
import SingleSelectField from "./fields/SingleSelectField";
import TextAreaField from "./fields/TextAreaField";
import TextField from "./fields/TextField";
import ToggleField from "./fields/ToggleField";
import FieldShell from "./FieldShell";
import type { FieldControlProps } from "./fieldTypes";
import type { FormFieldKind } from "./formTypes";

/**
 * Which control draws which kind of field.
 *
 * A registry keyed on the discriminator, so
 * `FormFieldKind` being a closed union means TypeScript points at this table the day a
 * new kind is added, rather than the card silently rendering a field with nothing in it.
 */
const CONTROLS: Record<FormFieldKind, ComponentType<FieldControlProps>> = {
  product: ProductField,
  multi_chips: MultiChipsField,
  single_select: SingleSelectField,
  text: TextField,
  textarea: TextAreaField,
  money: MoneyField,
  date: DateField,
  toggle: ToggleField,
};

type Props = Omit<FieldControlProps, "invalid"> & {
  register: (key: string, node: ComponentRef<typeof View> | null) => void;
};

const FormField = ({ error, register, ...control }: Props) => {
  const Control = CONTROLS[control.field.kind];
  return (
    <FieldShell
      field={control.field}
      error={error}
      hideLabel={control.field.kind === "toggle"}
      register={register}
    >
      <Control {...control} invalid={!!error} error={error} />
    </FieldShell>
  );
};

export default FormField;
